import { Asset } from '@/api/generatedApi';
import { CriticalGeometryArtifactEntry } from '@/api/api';
import { useGetCriticalGeometryArtifacts } from '@/hooks/api/assets/useGetCriticalGeometryArtifacts';
import { useDeleteSupplementalAssets } from '@/hooks/api/assets/useDeleteSupplementalAssets';
import { useGetSupplementalAssets } from '@/hooks/api/assets/useGetSupplementalAssets';
import { useUnlinkCriticalGeometryArtifact } from '@/hooks/api/assets/useUnlinkCriticalGeometryArtifact';
import { useTenants } from '@/services/tenants/useTenants';
import { getAssetFileName } from '@/utils/assets';
import { Box, CircularProgress, IconButton, Skeleton, Tooltip } from '@mui/material';
import { createUseStyles } from '@/services/createUseStyles';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import { Delete } from '@mui/icons-material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const useStyles = createUseStyles(() => ({
  SupplementalAssetsList_TreeView: {
    paddingLeft: '.5rem',
    '& .MuiTreeItem-iconContainer': {
      display: 'none',
    },
  },
  SupplementalAssetsList_label: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: '10px 0',
    height: '1.5rem',
  },
  SupplementalAssetsList_text: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
    fontSize: '0.875rem',
    color: 'inherit',
  },
  SupplementalAssetsList_deleteButton: {
    padding: '4px',
    marginLeft: '8px',
    '& .MuiSvgIcon-root': {
      fontSize: '1rem',
    },
  },
  SupplementalAssetsList_filename: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'inline-block',
    maxWidth: '100%',
  },
  SupplementalAssetsList_LoadState: {
    marginLeft: '15px',
    height: '32px',
  },
}));

type SidebarRow =
  | { type: 'supplemental'; supplementalAsset: Asset }
  | { type: 'criticalGeometryArtifact'; entry: CriticalGeometryArtifactEntry };

const buildRows = (supplementalAssets: Asset[], artifacts: CriticalGeometryArtifactEntry[]): SidebarRow[] => {
  const artifactsBySupersededId = new Map(
    artifacts.flatMap(entry =>
      entry.supersededSupplementalAssetId ? [[entry.supersededSupplementalAssetId, entry]] : []
    )
  );
  const listedSupplementalIds = new Set(supplementalAssets.map(supplementalAsset => supplementalAsset.id));
  const supplementalRows = supplementalAssets.map((supplementalAsset): SidebarRow => {
    const entry = artifactsBySupersededId.get(supplementalAsset.id);
    return entry ? { type: 'criticalGeometryArtifact', entry } : { type: 'supplemental', supplementalAsset };
  });
  const artifactOnlyRows = artifacts
    .filter(
      entry => !entry.supersededSupplementalAssetId || !listedSupplementalIds.has(entry.supersededSupplementalAssetId)
    )
    .map((entry): SidebarRow => ({ type: 'criticalGeometryArtifact', entry }));
  return [...supplementalRows, ...artifactOnlyRows];
};

const RemoveButton = ({
  fileName,
  isRemoving,
  onRemove,
}: {
  fileName: string;
  isRemoving: boolean;
  onRemove: (event: React.MouseEvent) => void;
}) => {
  const c = useStyles();
  return (
    <Tooltip title='Remove Supplemental Asset'>
      {isRemoving ? (
        <IconButton size='small' disabled>
          <CircularProgress size={16} />
        </IconButton>
      ) : (
        <IconButton
          className={c.SupplementalAssetsList_deleteButton}
          onClick={onRemove}
          size='small'
          color='primary'
          aria-label={`Remove ${fileName}`}
        >
          <Delete />
        </IconButton>
      )}
    </Tooltip>
  );
};

export const SupplementalAssetsList = ({ asset }: { asset?: Asset }) => {
  const { supplementalAssets, isLoading, error } = useGetSupplementalAssets({
    assetId: asset?.id,
    assetTenantId: asset?.tenantId,
    enabled: !!asset?.hasSupplementalAssets,
  });
  const { criticalGeometryArtifacts, isLoading: isLoadingArtifacts } = useGetCriticalGeometryArtifacts({
    assetId: asset?.id,
    assetTenantId: asset?.tenantId,
  });

  const { deleteSupplementalAssets, isDeleting } = useDeleteSupplementalAssets();
  const { unlinkCriticalGeometryArtifact, isUnlinking } = useUnlinkCriticalGeometryArtifact();
  const [deletingId, setDeletingId] = useState<string>();
  const { getPath } = useTenants();
  const navigate = useNavigate();
  const c = useStyles();

  const handleDelete = (supplementalAssetId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (!asset?.id) return;
    setDeletingId(supplementalAssetId);
    deleteSupplementalAssets(
      {
        assetId: asset.id,
        supplementalAssetIds: [supplementalAssetId],
      },
      {
        onSettled: () => setDeletingId(undefined),
      }
    );
  };

  const handleUnlink = (artifactId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (!asset?.id) return;
    setDeletingId(artifactId);
    unlinkCriticalGeometryArtifact(
      { tenantId: asset.tenantId, assetId: asset.id, artifactId },
      {
        onSettled: () => setDeletingId(undefined),
      }
    );
  };

  const handleItemClick = (supplementalAssetId: string) => {
    navigate(getPath(`asset/${supplementalAssetId}`));
  };

  const handleArtifactClick = (artifactId: string) => {
    navigate(getPath(`asset/${asset?.id}/critical-geometry/${artifactId}`));
  };

  if (error) {
    return;
  }

  if (isLoading || isLoadingArtifacts) {
    return (
      <Skeleton
        data-testid='supplemental-assets-skeleton'
        width={180}
        className={c.SupplementalAssetsList_LoadState}
        variant='text'
      />
    );
  }

  const rows = buildRows((supplementalAssets ?? []) as Asset[], criticalGeometryArtifacts);
  if (rows.length === 0) {
    return;
  }

  return (
    <SimpleTreeView
      aria-label='supplemental assets'
      data-testid='supplemental-assets-list'
      className={c.SupplementalAssetsList_TreeView}
    >
      {rows.map(row => {
        if (row.type === 'criticalGeometryArtifact') {
          return (
            <TreeItem
              key={row.entry.artifactId}
              itemId={row.entry.artifactId}
              label={
                <Box
                  className={c.SupplementalAssetsList_label}
                  onClick={event => {
                    event.stopPropagation();
                    handleArtifactClick(row.entry.artifactId);
                  }}
                  sx={{ cursor: 'pointer', color: 'text.primary' }}
                >
                  <Box className={c.SupplementalAssetsList_text}>
                    <span className={c.SupplementalAssetsList_filename}>{row.entry.name}</span>
                  </Box>
                  <RemoveButton
                    fileName={row.entry.name}
                    isRemoving={isUnlinking && deletingId === row.entry.artifactId}
                    onRemove={event => handleUnlink(row.entry.artifactId, event)}
                  />
                </Box>
              }
            />
          );
        }
        const asset = row.supplementalAsset;
        return (
          <TreeItem
            key={asset.id}
            itemId={asset.id}
            label={
              <Box
                className={c.SupplementalAssetsList_label}
                onClick={event => {
                  event.stopPropagation();
                  handleItemClick(asset.id);
                }}
                sx={{ cursor: 'pointer', color: 'text.primary' }}
              >
                <Box className={c.SupplementalAssetsList_text}>
                  <span className={c.SupplementalAssetsList_filename}>{getAssetFileName(asset)}</span>
                </Box>
                <RemoveButton
                  fileName={getAssetFileName(asset)}
                  isRemoving={isDeleting && deletingId === asset.id}
                  onRemove={event => handleDelete(asset.id, event)}
                />
              </Box>
            }
          />
        );
      })}
    </SimpleTreeView>
  );
};
